<template>
  <div class="dashboard">
    <!-- Sidebar -->
    <Sidebar @toggle="handleSidebarToggle" />

    <!-- Main Content -->
    <main
      class="main-content"
      :class="{ 'main-content-expanded': !isSidebarCollapsed }"
    >
      <!-- Header -->
      <header class="header">
        <div class="greeting">
          <WaitingDriversNumber :waiting-captains="waitingCaptains" />
        </div>
        <div class="header-icons">
          <i class="icon notifications"></i>
          <i class="icon menu"></i>
        </div>
      </header>

      <!-- Loading State -->
      <div v-if="loading" class="loading-overlay">
        <div class="loading-spinner"></div>
      </div>

      <!-- Error State -->
      <div v-if="error" class="error-message">
        {{ error }}
        <button @click="getDrivers">Retry</button>
      </div>

      <!-- Waiting List Section -->
      <section class="waiting-list">
        <div class="table-header">
          <h2>Captains Waiting List</h2>
          <div class="table-controls">
            <input
              type="text"
              placeholder="Search by Name, Phone, or National ID"
              v-model="searchWaitingQuery"
            />
          </div>
        </div>

        <!-- Waiting Table -->
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  v-model="selectAll"
                  @change="toggleSelectAll"
                />
              </th>
              <th>Photo</th>
              <th>Name</th>
              <th>National ID</th>
              <th>City</th>
              <th>Phone Number</th>
              <th>Vehicle</th>
              <th>Model</th>
              <th>Date Of Certain License</th>
              <th>Document</th>
              <th>Captains Account</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="waitingCaptain in filteredWaitingCaptains"
              :key="waitingCaptain._id"
              class="clickable-row"
              @click="goToDriverDetails(waitingCaptain._id)"
            >
              <td @click.stop>
                <input type="checkbox" v-model="waitingCaptain.selected" />
              </td>
              <td>
                <img
                  :src="
                    waitingCaptain.profile_image ||
                    'https://via.placeholder.com/40'
                  "
                  alt="Captain Photo"
                  class="captain-photo"
                />
              </td>
              <td>{{ waitingCaptain.username }}</td>
              <td>{{ waitingCaptain.id }}</td>
              <td>{{ waitingCaptain.city || "N/A" }}</td>
              <td>{{ waitingCaptain.phoneNumber || "N/A" }}</td>
              <td>{{ waitingCaptain.vehicleType || "N/A" }}</td>
              <td>{{ waitingCaptain.carModel || "N/A" }}</td>
              <td>{{ waitingCaptain.licence_expire_date || "N/A" }}</td>
              <td @click.stop>
                <button
                  class="action-open"
                  @click="openDocument(waitingCaptain)"
                >
                  Open
                </button>
              </td>
              <td @click.stop>
                <router-link
                  :to="{
                    name: 'DriverDetails',
                    params: { driverId: waitingCaptain._id },
                  }"
                  class="action-open"
                >
                  View Details
                </router-link>
                <span class="action-disable"> Disabled </span>
              </td>
            </tr>
            <tr v-if="filteredWaitingCaptains.length === 0">
              <td colspan="11" class="no-data">No waiting captains found</td>
            </tr>
          </tbody>
        </table>

        <!-- Waiting Table Footer -->
        <div class="table-footer">
          <div>
            <p>Total Captains Waiting: {{ backendTotalDrivers }}</p>
            <p>Active Requests: {{ activeWaiting }}</p>
          </div>
          <div class="pagination">
            <span>
              {{ (backendCurrentPage - 1) * backendPerPage + 1 }} -
              {{
                Math.min(
                  backendCurrentPage * backendPerPage,
                  backendTotalDrivers
                )
              }}
              of {{ backendTotalDrivers }} items
            </span>

            <button
              :disabled="backendCurrentPage === 1"
              @click="getDrivers(backendCurrentPage - 1)"
            >
              ❮
            </button>

            <button>{{ backendCurrentPage }}</button>

            <button
              :disabled="backendCurrentPage >= backendTotalPages"
              @click="getDrivers(backendCurrentPage + 1)"
            >
              ❯
            </button>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import Sidebar from "./sidebarComponent.vue";
import axios from "@/axios";
import WaitingDriversNumber from "@/components/waitingDriversNumber.vue";

export default {
  components: {
    WaitingDriversNumber,
    Sidebar,
  },
  data() {
    return {
      isSidebarCollapsed: false,
      waitingCaptains: [],
      searchWaitingQuery: "",
      adminName: localStorage.getItem("username") || "Admin",
      backendCurrentPage: 1,
      backendPerPage: 20,
      backendTotalPages: 1,
      backendTotalDrivers: 0,
      useClientPagination: false, // نفعّل التقسيم محليًا
      allWaitingCaptains: [],
      baseUrl: "https://backend.fego-rides.com/admin",
      activeWaiting: 0,
      loading: false,
      error: null,
      selectAll: false,
    };
  },
  computed: {
    filteredWaitingCaptains() {
      // في وضع السيرفر: فلتر الصفحة الحالية فقط
      const source = this.waitingCaptains;
      const q = (this.searchWaitingQuery || "").toLowerCase();
      if (!q) return source;

      return source.filter(
        (c) =>
          c.username?.toLowerCase().includes(q) ||
          c.phoneNumber?.toLowerCase().includes(q) ||
          String(c.id || "")
            .toLowerCase()
            .includes(q)
      );
    },
  },

  methods: {
    handleSidebarToggle(collapsed) {
      this.isSidebarCollapsed = collapsed;
    },
    handleResize() {
      if (window.innerWidth <= 768) {
        this.isSidebarCollapsed = true;
      } else {
        this.isSidebarCollapsed = false;
      }
    },
    stableSort(a, b) {
      const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (da !== db) return da - db;
      const ida = a._id || "";
      const idb = b._id || "";
      return ida.localeCompare(idb);
    },

    dedupeSmart(list) {
      // بنكوّن مفتاح فريد بالترتيب: _id ثم National ID ثم phoneNumber
      const seen = new Set();
      return list.filter((it) => {
        const key =
          it && (it._id || it.id || it.phoneNumber)
            ? String(it._id || it.id || it.phoneNumber).trim()
            : null;
        if (!key) return false;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },

    changePage(page) {
      if (page < 1 || page > this.backendTotalPages) return;
      this.getDrivers(page);
    },

    async loadAllDriversOnce() {
      this.loading = true;
      this.error = null;
      try {
        // 1) هات الصفحة الأولى لمعرفة إجمالي الصفحات
        const first = await axios.get(`${this.baseUrl}/get-drivers`, {
          params: {
            waiting: "waiting",
            page: 1,
            driversPerPage: this.backendPerPage,
            sortBy: "createdAt",
            order: "asc",
          },
        });

        const firstDrivers = Array.isArray(first.data)
          ? first.data
          : first.data.drivers || [];
        const pagination = first.data.pagination || {
          totalPages: 1,
          totalDrivers: firstDrivers.length,
        };

        // 2) هات باقي الصفحات
        const reqs = [];
        for (let p = 2; p <= (pagination.totalPages || 1); p++) {
          reqs.push(
            axios.get(`${this.baseUrl}/get-drivers`, {
              params: {
                waiting: "waiting",
                page: p,
                driversPerPage: this.backendPerPage,
                sortBy: "createdAt",
                order: "asc",
              },
            })
          );
        }
        const rest = await Promise.all(reqs);
        const restDrivers = rest.flatMap((r) =>
          Array.isArray(r.data) ? r.data : r.data.drivers || []
        );

        // 3) فلترة شروطك هنا (مرّة واحدة)
        const merged = [...firstDrivers, ...restDrivers].filter(
          (d) => d && d.block === false && (d.ctr ?? 0) === 0
        );

        // 4) إزالة التكرار + ترتيب ثابت
        const uniqueSorted = this.dedupeSmart(merged).sort(this.stableSort);
        // 5) خزّن المصدر الكامل واعرض الصفحة الحالية
        this.allWaitingCaptains = uniqueSorted.map((d) => ({
          ...d,
          selected: false,
        }));
        this.backendTotalDrivers = this.allWaitingCaptains.length;
        this.backendTotalPages =
          Math.ceil(this.backendTotalDrivers / this.backendPerPage) || 1;
        if (this.backendCurrentPage > this.backendTotalPages)
          this.backendCurrentPage = this.backendTotalPages || 1;
        if (this.backendCurrentPage < 1) this.backendCurrentPage = 1;
        this.updateClientPageView();
      } catch (err) {
        this.error =
          err?.response?.data?.message ||
          "Failed to load waiting captains. Please try again later.";
      } finally {
        this.loading = false;
      }
    },

    async getDrivers(page = this.backendCurrentPage) {
      if (this.useClientPagination) {
        // في الوضع المحلي: بس غيّر الصفحة الظاهرة
        this.backendCurrentPage = page;
        this.updateClientPageView();
        return;
      }

      // (احتياطي لو رجعت لـ server-side)
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(`${this.baseUrl}/get-drivers`, {
          params: {
            waiting: "waiting",
            page,
            driversPerPage: this.backendPerPage,
          },
        });
        const drivers = Array.isArray(data) ? data : data.drivers || [];
        const filtered = drivers.filter(
          (d) => d && d.block === false && (d.ctr ?? 0) === 0
        );
         filtered.sort(this.stableSort); // in-place
        const unique = this.dedupeSmart(filtered);
        this.waitingCaptains = unique.map((d) => ({ ...d, selected: false }));

        if (data.pagination) {
          this.backendCurrentPage = data.pagination.currentPage || 1;
          this.backendPerPage =
            data.pagination.driversPerPage || this.backendPerPage;
          this.backendTotalPages = data.pagination.totalPages || 1;
          this.backendTotalDrivers =
            data.pagination.totalDrivers || this.waitingCaptains.length;
        }

        this.activeWaiting = this.waitingCaptains.filter(
          (c) => (c.status || "").toLowerCase() === "online"
        ).length;
      } catch (err) {
        this.error =
          err?.response?.data?.message ||
          "Failed to load waiting captains. Please try again later.";
      } finally {
        this.loading = false;
      }
    },

    async toggleCaptainStatus(captain) {
      this.loading = true;
      this.error = null;
      try {
        // ابعت الطلب للسيرفر
        await axios.post(`${this.baseUrl}/update-driver-status`, {
          driverId: captain._id,
          block: !captain.block,
        });

        // بدل ما نغيّر القيمة محلي، نرجّع نجيب الصفحة من السيرفر
        await this.getDrivers(this.backendCurrentPage);
      } catch (err) {
        this.error = "Failed to update captain status. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    openDocument(captain) {
      // Placeholder action for opening a document
      // In a real app, this could open a modal, download a file, or navigate to a URL
      if (captain.documentUrl) {
        window.open(captain.documentUrl, "_blank");
      } else {
        alert(`No document available for ${captain.username}.`);
        console.log("Document action for captain:", captain.id);
      }
    },
    goToDriverDetails(driverId) {
      this.$router.push({ name: "DriverDetails", params: { driverId } });
    },
    toggleSelectAll() {
      this.filteredWaitingCaptains.forEach((c) => {
        c.selected = this.selectAll;
      });
    },
  },
  watch: {
    searchWaitingQuery() {
      this.backendCurrentPage = 1;
      this.selectAll = false;
    },
    waitingCaptains: {
      handler() {
        const visible = this.filteredWaitingCaptains;
        this.selectAll = visible.length > 0 && visible.every((c) => c.selected);
      },
      deep: true,
    },
  },
  created() {
    if (this.useClientPagination) {
      this.loadAllDriversOnce(); // نسحب الكل مرة واحدة
    } else {
      this.getDrivers(); // الوضع القديم
    }
  },
  mounted() {
    this.handleResize();
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
  },
};
</script>

<style scoped>
.dashboard {
  display: flex;
  height: 100vh;
  font-family: "Arial", sans-serif;
}

.main-content {
  margin-left: 250px;
  flex: 1;
  background-color: #f5f7fa;
  padding: 20px;
  border-radius: 20px 0 0 20px;
  transition: margin-left 0.3s ease;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.greeting h1 {
  font-size: 1.5rem;
  margin: 0;
}

.greeting p {
  color: #888;
  margin: 5px 0 0;
}

.wave {
  font-size: 1.2rem;
}

.header-icons i {
  font-size: 1.5rem;
  margin-left: 15px;
  cursor: pointer;
}

.waiting-list {
  background-color: white;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.table-header h2 {
  font-size: 1.2rem;
}

.table-controls {
  display: flex;
  gap: 10px;
}

.table-controls input {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 5px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  padding: 10px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

th {
  font-weight: bold;
  color: #333;
}

.clickable-row {
  cursor: pointer;
}

.clickable-row:hover {
  background-color: #f5f7fa;
}

.captain-photo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.action-open,
.action-disable {
  padding: 5px 10px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.action-open {
  background-color: #e6f7fa;
  color: #00cfe8;
  margin-right: 5px;
  text-decoration: none;
}

.action-disable {
  background-color: #ffebee;
  color: #ff4d4f;
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pagination button {
  padding: 5px 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background-color: #6b48ff;
  color: white;
  cursor: pointer;
}

.pagination button:disabled {
  background-color: #ddd;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 80px;
  }

  .main-content-expanded {
    margin-left: 250px;
  }
}

@media (min-width: 769px) {
  .main-content {
    margin-left: 250px !important;
  }
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loading-spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #6b48ff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-message {
  background-color: #ffebee;
  color: #ff4d4f;
  padding: 15px;
  margin: 20px 0;
  border-radius: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.error-message button {
  background-color: #ff4d4f;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 3px;
  cursor: pointer;
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #888;
}

.main-content-expanded {
  margin-left: 250px;
}
</style>
